import React, { useEffect, useState } from 'react';
import './Login.css';
import { apiUrl } from '../../api/client';
import { LoginForm } from './components/LoginForm';
import { SignupForm } from './components/SignupForm';
import { TryForm } from './components/TryForm';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiRoutes } from '../../types/ApiRoutes';

type FormType = 'login' | 'signup' | 'try';

const getFormTypeFromHash = (hash: string): FormType => {
  if (hash === '#signup') {
    return 'signup';
  }
  if (hash === '#try') {
    return 'try';
  }
  return 'login';
};

const Login: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [formType, setFormType] = useState<FormType>(() => getFormTypeFromHash(location.hash));
  const [loadingStatus, setLoadingStatus] = useState(true);

  const switchForm = (type: FormType) => {
    setFormType(type);
    navigate(`#${type}`, { replace: true });
  };
  const [signupEnabled, setSignupEnabled] = useState(false);
  const [trialEnabled, setTrialEnabled] = useState(false);

  // Helpers
  useEffect(() => {
    document.title = 'Walk It With Me - Login';

    // Fetch signup/trial status once
    const fetchSignupStatus = async () => {
      try {
        const response = await fetch(apiUrl(ApiRoutes.SignupStatus));
        if (response.ok) {
          const data: { isSignupEnabled: boolean; isTrialEnabled: boolean } = await response.json();
          setSignupEnabled(!!data.isSignupEnabled);
          setTrialEnabled(!!data.isTrialEnabled);
        } else {
          setSignupEnabled(false);
          setTrialEnabled(false);
        }
      } catch {
        setSignupEnabled(false);
        setTrialEnabled(false);
      } finally {
        setLoadingStatus(false);
      }
    };

    void fetchSignupStatus();
  }, []);

  // Clear any existing error when switching between forms
  useEffect(() => {
    // No shared error state anymore
  }, [formType]);

  const renderForm = () => {
    switch (formType) {
      case 'login':
        return <LoginForm />;
      case 'signup':
        return <SignupForm loadingStatus={loadingStatus} signupEnabled={signupEnabled} />;
      case 'try':
        return <TryForm loadingStatus={loadingStatus} trialEnabled={trialEnabled} />;
      default:
        return null;
    }
  };

  return (
    <>
      <div className="container py-5 login-page">
        <div className="row justify-content-center">
          <div className="col-12 col-sm-10 col-md-8 col-lg-6 col-xl-5">
            <div className="card shadow-sm">
              <div className="card-body p-4">
                <h1 className="h3 mb-4 text-center">Welcome to Walk It With Me</h1>
                <div
                  className="btn-group d-flex mb-4"
                  role="tablist"
                  aria-label="Authentication forms"
                >
                  <button
                    id="tab-login"
                    type="button"
                    className={`btn ${formType === 'login' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => switchForm('login')}
                    role="tab"
                    aria-selected={formType === 'login' ? true : undefined}
                    aria-controls="panel-login"
                  >
                    Login
                  </button>
                  <button
                    id="tab-signup"
                    type="button"
                    className={`btn ${formType === 'signup' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => switchForm('signup')}
                    role="tab"
                    aria-selected={formType === 'signup' ? true : undefined}
                    aria-controls="panel-signup"
                  >
                    Sign Up
                  </button>
                  <button
                    id="tab-try"
                    type="button"
                    className={`btn ${formType === 'try' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => switchForm('try')}
                    role="tab"
                    aria-selected={formType === 'try' ? true : undefined}
                    aria-controls="panel-try"
                  >
                    Try
                  </button>
                </div>
                <div
                  role="tabpanel"
                  id="panel-login"
                  aria-labelledby="tab-login"
                  hidden={formType !== 'login'}
                >
                  {formType === 'login' && renderForm()}
                </div>
                <div
                  role="tabpanel"
                  id="panel-signup"
                  aria-labelledby="tab-signup"
                  hidden={formType !== 'signup'}
                >
                  {formType === 'signup' && renderForm()}
                </div>
                <div
                  role="tabpanel"
                  id="panel-try"
                  aria-labelledby="tab-try"
                  hidden={formType !== 'try'}
                >
                  {formType === 'try' && renderForm()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
